import type { QueryClient } from '@tanstack/react-query'
import { lazy, Suspense, useEffect, useEffectEvent, useState } from 'react'
import {
  createRootRouteWithContext,
  HeadContent,
  Scripts,
} from '@tanstack/react-router'
import { createServerFn } from '@tanstack/react-start'
import { getRequestHeader } from '@tanstack/react-start/server'

import { Toaster } from '@repo/ui/components/sonner'
import globalCss from '@repo/ui/styles/globals.css?url'

import type {
  MtgColorTheme,
  ResolvedTheme,
  Theme,
} from '../contexts/ThemeContext.js'
import { BetaBanner } from '../components/BetaBanner.js'
import { ErrorPage } from '../components/ErrorPage.js'
import { NotFoundPage } from '../components/NotFoundPage.js'
import { AuthProvider } from '../contexts/AuthContext.js'
import {
  parseThemeFromCookies,
  ThemeProvider,
} from '../contexts/ThemeContext.js'
import { ConvexProvider } from '../integrations/convex/provider.js'

export interface MyRouterContext {
  queryClient: QueryClient
}

const getThemeFromCookies = createServerFn({ method: 'GET' }).handler(
  async () => {
    const cookieHeader = getRequestHeader('Cookie') ?? ''
    return parseThemeFromCookies(cookieHeader)
  },
)

const siteOrigin = 'https://spelltablez.maxais.se'
const siteUrl = `${siteOrigin}/`
const siteName = 'Spelltablez'
const siteDescription =
  'Play paper Magic: The Gathering remotely with video chat, game tracking, and manual card search. Free, browser-based, no downloads required.'
const seoAssetVersion = '2026-06-29'
const versionedAsset = (path: string) => `${path}?v=${seoAssetVersion}`
const ogImageUrl = `${siteOrigin}${versionedAsset('/og-image.png')}`
// Runs synchronously before React hydrates to apply the correct theme class
// and MTG attribute so there is no flash.  Reads from cookies; falls back to
// localStorage for one-time migration of pre-cookie preferences.
// Cookie names must stay in sync with THEME_COOKIE / MTG_THEME_COOKIE in
// ThemeContext.tsx.
const themeBootstrapScript = `
(() => {
  try {
    var C = document.cookie;
    function cv(n) {
      var m = C.match(new RegExp('(?:^|;\\\\s*)' + n + '=([^;]*)'));
      return m ? decodeURIComponent(m[1]) : null;
    }

    var theme = cv('sc-theme');
    if (theme !== 'light' && theme !== 'dark' && theme !== 'system') theme = 'dark';

    var resolved = theme === 'system'
      ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
      : theme;

    var root = document.documentElement;
    if (resolved === 'dark') root.classList.add('dark');
    else root.classList.remove('dark');

    var mtg = cv('sc-mtg-theme');
    if (mtg === 'white' || mtg === 'blue' || mtg === 'black' || mtg === 'red' || mtg === 'green') {
      root.setAttribute('data-mtg-theme', mtg);
    } else {
      root.removeAttribute('data-mtg-theme');
    }
  } catch(e) {}
})();
`

export const Route = createRootRouteWithContext<MyRouterContext>()({
  loader: async () => {
    const themeSnapshot =
      typeof document !== 'undefined'
        ? parseThemeFromCookies(document.cookie)
        : await getThemeFromCookies()
    return { themeSnapshot }
  },
  notFoundComponent: NotFoundPage,
  errorComponent: ErrorPage,
  head: () => ({
    meta: [
      {
        charSet: 'utf-8',
      },
      {
        name: 'viewport',
        content: 'width=device-width, initial-scale=1',
      },
      {
        title: siteName,
      },
      {
        name: 'description',
        content: siteDescription,
      },
      // Open Graph / Facebook
      {
        property: 'og:type',
        content: 'website',
      },
      {
        property: 'og:url',
        content: siteUrl,
      },
      {
        property: 'og:title',
        content: siteName,
      },
      {
        property: 'og:description',
        content: siteDescription,
      },
      {
        property: 'og:image',
        content: ogImageUrl,
      },
      {
        property: 'og:image:secure_url',
        content: ogImageUrl,
      },
      {
        property: 'og:image:type',
        content: 'image/png',
      },
      {
        property: 'og:image:width',
        content: '1200',
      },
      {
        property: 'og:image:height',
        content: '630',
      },
      {
        property: 'og:image:alt',
        content: 'Spelltablez — Play Paper MTG Anywhere',
      },
      {
        property: 'og:site_name',
        content: siteName,
      },
      // Twitter / X
      {
        name: 'twitter:card',
        content: 'summary_large_image',
      },
      {
        name: 'twitter:url',
        content: siteUrl,
      },
      {
        name: 'twitter:title',
        content: siteName,
      },
      {
        name: 'twitter:description',
        content: siteDescription,
      },
      {
        name: 'twitter:image',
        content: ogImageUrl,
      },
      {
        name: 'twitter:image:alt',
        content: 'Spelltablez — Play Paper MTG Anywhere',
      },
      // Additional meta tags for better SEO and social sharing
      {
        name: 'theme-color',
        content: '#0f172a',
      },
      {
        name: 'apple-mobile-web-app-title',
        content: siteName,
      },
      {
        name: 'application-name',
        content: siteName,
      },
    ],
    links: [
      {
        rel: 'stylesheet',
        href: globalCss,
      },
      {
        rel: 'icon',
        type: 'image/png',
        sizes: '96x96',
        href: versionedAsset('/favicon-96x96.png'),
      },
      {
        rel: 'shortcut icon',
        type: 'image/x-icon',
        href: versionedAsset('/favicon.ico'),
      },
      {
        rel: 'apple-touch-icon',
        sizes: '180x180',
        href: versionedAsset('/apple-touch-icon.png'),
      },
      {
        rel: 'manifest',
        href: versionedAsset('/site.webmanifest'),
      },
      {
        rel: 'canonical',
        href: siteUrl,
      },
    ],
  }),

  shellComponent: RootDocument,
})

// Development only. `import.meta.env.DEV` is replaced at build time, so a
// production build drops this import expression and never emits the chunk -
// which is the point: see components/Devtools.tsx for why letting it reach
// the server bundle returned 500 on every server-rendered game room.
const Devtools = import.meta.env.DEV
  ? lazy(() => import('../components/Devtools.js'))
  : null

function resolveServerTheme(theme: Theme): ResolvedTheme {
  // Server cannot resolve 'system'; default to 'dark'.
  return theme === 'system' ? 'dark' : theme
}

function RootDocument({ children }: { children: React.ReactNode }) {
  const { themeSnapshot } = Route.useLoaderData()

  const serverResolved = resolveServerTheme(themeSnapshot.theme)
  const htmlClass = serverResolved === 'dark' ? 'dark' : undefined
  const mtgAttr: MtgColorTheme | undefined =
    themeSnapshot.mtgTheme !== 'none' ? themeSnapshot.mtgTheme : undefined

  const [showDevtools, setShowDevtools] = useState(
    import.meta.env.MODE === 'development',
  )

  const suppressDevtools = useEffectEvent(() => {
    setShowDevtools(false)
  })

  useEffect(() => {
    if (navigator.webdriver === true) {
      suppressDevtools()
    }

    // @ts-expect-error: a console escape hatch, not part of the DOM typings.
    // Development only now - a production build ships no devtools chunk for
    // this to reveal (see the Devtools import above for why).
    window.toggleDevtools = () => setShowDevtools((old) => !old)
  }, [])

  return (
    <html
      lang="en"
      className={htmlClass}
      data-mtg-theme={mtgAttr}
      suppressHydrationWarning
    >
      <head>
        <script>{themeBootstrapScript}</script>
        <HeadContent />
      </head>
      <body className="bg-surface-0 min-h-screen">
        <ThemeProvider
          defaultTheme={themeSnapshot.theme}
          defaultMtgTheme={themeSnapshot.mtgTheme}
        >
          <ConvexProvider>
            <AuthProvider>
              <div className="flex h-dvh flex-col overflow-hidden">
                <BetaBanner />
                <main className="flex min-h-0 flex-1 flex-col overflow-auto">
                  {children}
                </main>
              </div>
              <Suspense fallback={null}>
                {showDevtools && Devtools && <Devtools />}
              </Suspense>
              {/*
                One Toaster for the whole app. It used to be mounted inside
                GameRoom and LandingPage, which meant `toast()` from anywhere
                else rendered into nothing - notably the sign-in dialog in
                `_authed.tsx`, which is shown *instead of* either of those.
                A toast that goes nowhere is worse than no toast: it reads as
                handled.
              */}
              <Toaster />
            </AuthProvider>
          </ConvexProvider>
        </ThemeProvider>
        <Scripts />
      </body>
    </html>
  )
}
